import { useState, type ReactNode } from 'react';
import {
    SCIENCE_DEFINITIONS,
    SHIP_DEFINITIONS,
    type PlayerRace,
    type ScienceCategory,
    type ScienceId,
} from '@kaeliux/shared';
import {
    calculateScienceCost,
    calculateScienceTime,
    canAffordCost,
    canResearchAdditionalScience,
    extractBuildingLevels,
    getMaxScienceLevel,
    getScienceRequiredFreeEnergy,
    getUnmetRequirements,
    hasRequiredFreeEnergy,
} from '@kaeliux/game-engine';
import UnmetRequirementsGrid, { unmetRequirementTile } from './UnmetRequirementsGrid';
import ResourceQuantities from './ResourceQuantities';
import { resourceCostGlyph } from './resourceCostIcons';
import { Button } from './ui/Button';
import { Chip } from './ui/Chip';
import { Panel } from './ui/Panel';
import { getScienceIcon } from '../utils/scienceIcons';
import { cn } from '../utils/cn';
import { formatDuration, formatFullNumber } from '../utils/formatters';
import { resourceEntries } from '../utils/resourceQuantities';

/** Doc section order from `docs/game-details/sciences.md`. */
export const SCIENCE_CATEGORY_ORDER: ScienceCategory[] = ['fundamental', 'engineering', 'warfare'];

export const SCIENCE_CATEGORY_TITLES: Record<ScienceCategory, string> = {
    fundamental: 'Fundamental',
    engineering: 'Engineering',
    warfare: 'Warfare',
};

function describeScience(scienceId: ScienceId, race: PlayerRace | undefined): string {
    const def = SCIENCE_DEFINITIONS[scienceId];
    if (scienceId === 'gravitationalManipulators' && race) {
        return `Allows the construction of the ${SHIP_DEFINITIONS[race][9].name}`;
    }
    return def.description;
}

export interface ScienceCardProps {
    scienceId: ScienceId;
    race: PlayerRace | undefined;
    resources: { metal: number; minerals: number; gas: number; energy: number; ozone: number };
    laboratoryLevel: number;
    speedMultiplier: number;
    scienceLevels: Partial<Record<ScienceId, number>>;
    buildingLevels: ReturnType<typeof extractBuildingLevels>;
    /** How many research items for this science are already queued. */
    queuedCount: number;
    queueFull: boolean;
    highlighted?: boolean;
    /** Queues one more level. Rejects with the server's reason on failure. */
    onResearch: () => Promise<void>;
    /** False hides the Research button, e.g. a Team planet member. */
    canManage?: boolean;
    /** Already-rendered queue cards for this science. */
    queueItems?: ReactNode;
    footer?: ReactNode;
}

/**
 * One science's card: level, next cost/time, prerequisites and the Research
 * action. Shared by a personal planet's Sciences page and the Team planet's
 * research screen, which differ only in where research is queued and whose
 * science levels count.
 */
export default function ScienceCard({
    scienceId,
    race,
    resources,
    laboratoryLevel,
    speedMultiplier,
    scienceLevels,
    buildingLevels,
    queuedCount,
    queueFull,
    highlighted,
    onResearch,
    canManage = true,
    queueItems,
    footer,
}: ScienceCardProps) {
    const def = SCIENCE_DEFINITIONS[scienceId];
    const level = scienceLevels[scienceId] ?? 0;
    const projectedLevel = level + queuedCount;
    const maxLevel = getMaxScienceLevel(scienceId);

    const [isRequesting, setIsRequesting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const atMax = projectedLevel >= maxLevel;
    const nextLevel = projectedLevel + 1;
    const nextCost = atMax ? null : calculateScienceCost(scienceId, nextLevel);
    const nextTimeSeconds = atMax ? null : calculateScienceTime(scienceId, nextLevel, laboratoryLevel, speedMultiplier);

    const unmetRequirements = getUnmetRequirements(def.requirements, buildingLevels, scienceLevels);
    const canPrereq = unmetRequirements.length === 0;
    const canAdditional = canResearchAdditionalScience(scienceId, scienceLevels);
    const affordable = nextCost ? canAffordCost(nextCost, resources) : false;
    const requiredFreeEnergy = atMax ? 0 : getScienceRequiredFreeEnergy(scienceId);
    const hasFreeEnergy = hasRequiredFreeEnergy(scienceId, resources.energy);
    const freeEnergyLabel = `Requires ${formatFullNumber(requiredFreeEnergy)} free Energy`;
    const showCostAmounts = nextCost !== null && (requiredFreeEnergy === 0 || resourceEntries(nextCost).length > 0);

    const disabled = atMax || !canPrereq || !canAdditional || queueFull || !hasFreeEnergy || !affordable || isRequesting;

    const disabledReason: ReactNode = atMax
        ? 'Max level'
        : !canPrereq
            ? null // shown as the full-width UnmetRequirementsGrid below instead
            : !canAdditional
                ? 'Only one Additional science may be researched'
                : queueFull
                    ? 'Queue full'
                    : !hasFreeEnergy
                        ? freeEnergyLabel
                        : !affordable
                            ? 'Insufficient resources'
                            : null;

    async function handleResearch() {
        setIsRequesting(true);
        setError(null);
        try {
            await onResearch();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Research failed');
        } finally {
            setIsRequesting(false);
        }
    }

    return (
        <Panel variant="solid" className="flex flex-col gap-3" data-testid="science-card" data-tutorial-id={`science-${scienceId}`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex-1 min-w-0 flex items-start gap-3">
                    <div
                        className={`w-24 h-24 shrink-0 rounded-tile border overflow-hidden ${highlighted ? 'border-2 border-status-warning animate-highlight-blink' : 'border-hairline'}`}
                    >
                        <img src={getScienceIcon(scienceId)} alt="" className="w-full h-full object-cover bg-surface-void" />
                    </div>

                    <div className="min-w-0">
                        <div className="flex items-baseline gap-2">
                            <h3 className="text-title text-content-primary">{def.name}</h3>
                        </div>
                        <p className="text-body text-content-muted">{describeScience(scienceId, race)}</p>
                        <span className="text-body text-content-muted">
                            Level {level}{queuedCount > 0 ? ` (+${queuedCount})` : ''} / {maxLevel}
                        </span>
                        {!atMax && nextCost && nextTimeSeconds !== null && (
                            <p className="text-body text-content-secondary mt-2">
                                <span className="text-content-muted mr-2">Next:</span>
                                {showCostAmounts && <ResourceQuantities amounts={nextCost} />}
                                {requiredFreeEnergy > 0 && (
                                    <span
                                        className={cn(
                                            'inline-flex items-center gap-1 whitespace-nowrap align-middle',
                                            showCostAmounts && 'ml-3',
                                            !hasFreeEnergy && 'text-status-danger',
                                        )}
                                        data-testid="science-required-free-energy"
                                        data-met={hasFreeEnergy}
                                    >
                                        {resourceCostGlyph('energy')}
                                        <span>{freeEnergyLabel}</span>
                                    </span>
                                )}
                                <span className="text-content-muted ml-2">· {formatDuration(nextTimeSeconds)}</span>
                            </p>
                        )}
                        {error && <Chip tone="danger" className="mt-1" role="alert">{error}</Chip>}
                    </div>
                </div>
                {canManage && (
                    <div className="flex flex-col items-start sm:items-end gap-1">
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={handleResearch}
                            disabled={disabled}
                            data-testid={`research-${scienceId}`}
                        >
                            {isRequesting ? 'Queuing…' : atMax ? 'Max' : 'Research'}
                        </Button>
                        {disabledReason && !isRequesting && (
                            <Chip tone="danger" data-testid="science-disabled-reason">{disabledReason}</Chip>
                        )}
                    </div>
                )}
            </div>
            {unmetRequirements.length > 0 && !isRequesting && (
                <UnmetRequirementsGrid tiles={unmetRequirements.map((req) => unmetRequirementTile(req, race))} />
            )}
            {queueItems && (
                <div className="flex flex-wrap gap-3 border-t border-hairline pt-2">
                    {queueItems}
                </div>
            )}
            {footer}
        </Panel>
    );
}
